import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Edit as EditIcon, Refresh as RefreshIcon } from "@mui/icons-material";
import { collection, doc, getDocs, setDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";
import { DEFAULT_EMAIL_TEMPLATES } from "../data/defaultEmailTemplates";
import {
  getTemplatePreviewValues,
  renderTemplateString,
  SYSTEM_TEMPLATE_PLACEHOLDERS,
} from "../utils/emailTemplateUtils";

const formatUpdatedAt = (value) => {
  if (!value) {
    return "";
  }

  if (typeof value?.toDate === "function") {
    return value.toDate().toLocaleString();
  }

  if (value instanceof Date) {
    return value.toLocaleString();
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toLocaleString();
};

const normalizeStoredTemplates = (snapshot) => {
  const items = {};

  snapshot.forEach((templateDoc) => {
    items[templateDoc.id] = {
      id: templateDoc.id,
      ...templateDoc.data(),
    };
  });

  return items;
};
const CommunicationTemplates = ({ embedded = false }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [formData, setFormData] = useState({
    subject: "",
    body: "",
  });

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError("");
      setWarning("");

      const isLocalhost =
        typeof window !== "undefined" &&
        ["localhost", "127.0.0.1"].includes(window.location.hostname);

      const requests = isLocalhost
        ? [
            Promise.resolve({ data: { templates: DEFAULT_EMAIL_TEMPLATES } }),
            getDocs(collection(db, "communicationTemplates")),
          ]
        : [
            httpsCallable(functions, "getEmailTemplateDefinitions")(),
            getDocs(collection(db, "communicationTemplates")),
          ];

      const [definitionsResult, storedSnapshot] =
        await Promise.allSettled(requests);

      const storedTemplates =
        storedSnapshot.status === "fulfilled"
          ? normalizeStoredTemplates(storedSnapshot.value)
          : {};

      const systemTemplates =
        definitionsResult.status === "fulfilled"
          ? definitionsResult.value.data?.templates || []
          : DEFAULT_EMAIL_TEMPLATES;

      if (isLocalhost) {
        setWarning(
          "Using the local template catalog while the dashboard runs on localhost. Deploy the new Cloud Function to load the catalog from Functions in production.",
        );
      } else if (definitionsResult.status !== "fulfilled") {
        console.warn(
          "Falling back to local email template catalog:",
          definitionsResult.reason,
        );
        setWarning(
          "Using the dashboard fallback catalog because the Cloud Function is not reachable yet. This is expected before you deploy the new function.",
        );
      }

      if (storedSnapshot.status !== "fulfilled") {
        throw storedSnapshot.reason;
      }

      const knownKeys = new Set(
        systemTemplates.map((template) => template.key),
      );

      const mergedSystemTemplates = systemTemplates.map((template) => {
        const stored =
          storedTemplates[template.key] ||
          storedTemplates[template.templateKey];
        const subject = stored?.subject || template.subject || "";
        const body = stored?.body || template.body || "";

        return {
          ...template,
          id: template.key,
          key: template.key,
          subject,
          body,
          defaultSubject: template.subject || "",
          defaultBody: template.body || "",
          updatedAt: stored?.updatedAt || null,
          customized:
            subject !== (template.subject || "") ||
            body !== (template.body || ""),
          isCustom: false,
        };
      });

      const customTemplates = Object.values(storedTemplates)
        .filter((template) => !knownKeys.has(template.id))
        .map((template) => ({
          id: template.id,
          key: template.id,
          name: template.name || template.id,
          category: template.category || "Custom",
          description: template.description || "Custom email template",
          variables: Array.isArray(template.variables)
            ? template.variables
            : [],
          subject: template.subject || "",
          body: template.body || "",
          defaultSubject: template.subject || "",
          defaultBody: template.body || "",
          updatedAt: template.updatedAt || null,
          customized: true,
          isCustom: true,
        }));

      const mergedTemplates = [
        ...mergedSystemTemplates,
        ...customTemplates,
      ].sort((left, right) => {
        const categoryCompare = String(left.category).localeCompare(
          String(right.category),
        );
        return categoryCompare !== 0
          ? categoryCompare
          : String(left.name).localeCompare(String(right.name));
      });

      setTemplates(mergedTemplates);
    } catch (err) {
      console.error("Error fetching email templates:", err);
      setError("Failed to load email templates. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const categories = useMemo(() => {
    const uniqueCategories = [
      ...new Set(templates.map((template) => template.category)),
    ];
    return ["All", ...uniqueCategories];
  }, [templates]);

  const filteredTemplates = useMemo(() => {
    return templates.filter((template) => {
      const matchesCategory =
        categoryFilter === "All" || template.category === categoryFilter;
      const matchesSearch =
        !searchTerm ||
        [
          template.name,
          template.category,
          template.description,
          template.subject,
          template.key,
        ]
          .join(" ")
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

      return matchesCategory && matchesSearch;
    });
  }, [categoryFilter, searchTerm, templates]);

  const previewValues = useMemo(() => {
    return editingTemplate ? getTemplatePreviewValues(editingTemplate) : {};
  }, [editingTemplate]);

  const renderedPreviewSubject = useMemo(() => {
    return renderTemplateString(formData.subject, previewValues);
  }, [formData.subject, previewValues]);

  const renderedPreviewBody = useMemo(() => {
    return renderTemplateString(formData.body, previewValues);
  }, [formData.body, previewValues]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: "", severity: "success" });
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      subject: template.subject || "",
      body: template.body || "",
    });
    setDialogOpen(true);
  };

  const handleRestoreDefaults = () => {
    if (!editingTemplate) {
      return;
    }

    setFormData({
      subject: editingTemplate.defaultSubject || "",
      body: editingTemplate.defaultBody || "",
    });
  };

  const updateTemplateLocally = (templateKey, nextValues) => {
    setTemplates((currentTemplates) =>
      currentTemplates.map((template) => {
        if (template.key !== templateKey) {
          return template;
        }

        return {
          ...template,
          ...nextValues,
          customized:
            (nextValues.subject ?? template.subject) !==
              template.defaultSubject ||
            (nextValues.body ?? template.body) !== template.defaultBody,
        };
      }),
    );
  };

  const handleSave = async () => {
    if (!editingTemplate) {
      return;
    }

    if (!formData.subject.trim() || !formData.body.trim()) {
      showSnackbar("Subject and body are required.", "error");
      return;
    }

    try {
      setSaving(true);

      const updatedTemplate = {
        templateKey: editingTemplate.key,
        name: editingTemplate.name,
        category: editingTemplate.category,
        description: editingTemplate.description,
        variables: editingTemplate.variables || [],
        subject: formData.subject,
        body: formData.body,
        system: !editingTemplate.isCustom,
        updatedAt: new Date(),
      };

      await setDoc(
        doc(db, "communicationTemplates", editingTemplate.key),
        updatedTemplate,
        { merge: true },
      );

      updateTemplateLocally(editingTemplate.key, {
        subject: formData.subject,
        body: formData.body,
        updatedAt: updatedTemplate.updatedAt,
      });

      setDialogOpen(false);
      showSnackbar("Email template saved successfully.");
    } catch (err) {
      console.error("Error saving email template:", err);
      showSnackbar("Failed to save email template.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleResetTemplate = async (template) => {
    try {
      setSaving(true);

      const resetTemplate = {
        templateKey: template.key,
        name: template.name,
        category: template.category,
        description: template.description,
        variables: template.variables || [],
        subject: template.defaultSubject,
        body: template.defaultBody,
        system: !template.isCustom,
        updatedAt: new Date(),
      };

      await setDoc(
        doc(db, "communicationTemplates", template.key),
        resetTemplate,
        { merge: true },
      );

      updateTemplateLocally(template.key, {
        subject: template.defaultSubject,
        body: template.defaultBody,
        updatedAt: resetTemplate.updatedAt,
      });

      if (editingTemplate?.key === template.key) {
        setFormData({
          subject: template.defaultSubject,
          body: template.defaultBody,
        });
      }

      showSnackbar("Template restored to default content.", "info");
    } catch (err) {
      console.error("Error resetting email template:", err);
      showSnackbar("Failed to restore template defaults.", "error");
    } finally {
      setSaving(false);
    }
  };

  const editorPlaceholders = editingTemplate
    ? [...(editingTemplate.variables || []), ...SYSTEM_TEMPLATE_PLACEHOLDERS]
    : [];

  return (
    <Box>
      {!embedded && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 3,
          }}
        >
          {/* <Box>
            <Typography variant="h4" gutterBottom>
              Email Template Editor
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage the live email formats used by bookings, OTP, refunds, reports, and provider notifications.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchTemplates}
            disabled={loading}
          >
            Refresh
          </Button> */}
        </Box>
      )}

      {/* {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {warning && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {warning}
        </Alert>
      )} */}

      {/* <Alert severity="info" sx={{ mb: 3 }}>
        These templates are used by Firebase Functions at send time. Changes
        here affect future emails without another code deploy.
      </Alert> */}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Search templates"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search by name, category, key, or subject"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            select
            fullWidth
            label="Category"
            value={categoryFilter}
            onChange={(event) => setCategoryFilter(event.target.value)}
            SelectProps={{ native: true }}
          >
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, md: 2 }}> 
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchTemplates}
          disabled={loading}
        >
          Refresh
        </Button>
        </Grid>
      </Grid>

      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 4 }}>
          <CircularProgress size={24} />
          <Typography>Loading email templates...</Typography>
        </Box>
      ) : filteredTemplates.length === 0 ? (
        <Card>
          <CardContent>
            <Typography color="text.secondary" align="center">
              No templates matched your filters.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {filteredTemplates.map((template) => (
            <Grid key={template.key} size={{ xs: 12, lg: 6 }}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ mb: 1, flexWrap: "wrap" }}
                  >
                    <Chip
                      label={template.category}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    <Chip
                      label={template.customized ? "Customized" : "Default"}
                      size="small"
                      color={template.customized ? "success" : "default"}
                    />
                    {template.isCustom && (
                      <Chip
                        label="Custom Record"
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    )}
                  </Stack>

                  <Typography variant="h6" gutterBottom>
                    {template.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    {template.description}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mb: 1 }}
                  >
                    Key: {template.key}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Subject:</strong> {template.subject}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      display: "-webkit-box",
                      WebkitLineClamp: 4,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      mb: 2,
                    }}
                  >
                    {template.body
                      .replace(/<[^>]+>/g, " ")
                      .replace(/\s+/g, " ")
                      .trim()}
                  </Typography>

                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ flexWrap: "wrap", mb: 1 }}
                  >
                    {(template.variables || []).slice(0, 6).map((variable) => (
                      <Chip
                        key={variable}
                        label={`{{${variable}}}`}
                        size="small"
                      />
                    ))}
                    {(template.variables || []).length > 6 && (
                      <Chip
                        label={`+${template.variables.length - 6} more`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Stack>

                  {template.updatedAt && (
                    <Typography variant="caption" color="text.secondary">
                      Last saved: {formatUpdatedAt(template.updatedAt)}
                    </Typography>
                  )}
                </CardContent>

                <CardActions>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleEdit(template)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    color="inherit"
                    onClick={() => handleResetTemplate(template)}
                    disabled={saving || !template.customized}
                  >
                    Restore Default
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          {editingTemplate?.name || "Edit Email Template"}
        </DialogTitle>
        <DialogContent>
          {editingTemplate && (
            <Grid container spacing={3} sx={{ pt: 1 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Stack spacing={2}>
                  <TextField
                    label="Template Key"
                    value={editingTemplate.key}
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    label="Subject"
                    value={formData.subject}
                    onChange={(event) =>
                      setFormData((current) => ({
                        ...current,
                        subject: event.target.value,
                      }))
                    }
                    fullWidth
                  />
                  <TextField
                    label="Body"
                    value={formData.body}
                    onChange={(event) =>
                      setFormData((current) => ({
                        ...current,
                        body: event.target.value,
                      }))
                    }
                    multiline
                    rows={18}
                    fullWidth
                    helperText="Use HTML plus placeholders like {{customerName}} or {{bookingId}}."
                  />

                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Available Placeholders
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ flexWrap: "wrap" }}
                    >
                      {editorPlaceholders.map((placeholder) => (
                        <Chip
                          key={placeholder}
                          label={`{{${placeholder}}}`}
                          size="small"
                        />
                      ))}
                    </Stack>
                  </Box>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Preview
                </Typography>
                <Card variant="outlined">
                  <CardContent>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 1 }}
                    >
                      Preview uses sample values for placeholders.
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 2 }}>
                      Subject: {renderedPreviewSubject}
                    </Typography>
                    <Box
                      sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                        p: 2,
                        backgroundColor: "#fafafa",
                        minHeight: 420,
                        overflow: "auto",
                      }}
                      dangerouslySetInnerHTML={{ __html: renderedPreviewBody }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRestoreDefaults} disabled={!editingTemplate}>
            Load Default
          </Button>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? "Saving..." : "Save Template"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CommunicationTemplates;
